import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import axios from 'axios';
import i18n from '../../i18n';
import { useAuthStore } from '../../store/auth.store';
// Runs once on every page load / browser refresh.
// Silently restores the user's session using the httpOnly refresh token cookie.
// While checking: renders <AppSkeleton /> to prevent flash of wrong UI state.
export function AuthProvider({ children }) {
    const { setAuth, clearAuth, setInitializing, isInitializing } = useAuthStore();
    useEffect(() => {
        async function restoreSession() {
            try {
                // Browser automatically sends the httpOnly cookie — no token in code needed
                const res = await axios.post(`${import.meta.env.VITE_API_URL}/auth/refresh`, {}, { withCredentials: true });
                setAuth(res.data.data.user, res.data.data.accessToken);
                // S14-17: apply server-side language preference on session restore.
                // Server truth wins over localStorage so signing in on a new device
                // gets the same UI language as the last one. `.locale` is stored as
                // an ISO code ("yo") or a fuller BCP-47 tag ("en-NG") — we take only
                // the primary subtag before the '-' since i18n bundles are named that
                // way (en / yo / ig / ha).
                const serverLocale = (res.data.data.user.locale ?? '').split('-')[0];
                const supported = ['en', 'yo', 'ig', 'ha'];
                if (serverLocale && supported.includes(serverLocale) && i18n.language !== serverLocale) {
                    void i18n.changeLanguage(serverLocale);
                    localStorage.setItem('gxl-language', serverLocale);
                }
            }
            catch (err) {
                clearAuth();
                // Show a single, non-looping message for permanently removed accounts
                const message = err
                    ?.response?.data?.message;
                if (message === 'ACCOUNT_DELETED') {
                    const { default: toast } = await import('react-hot-toast');
                    toast.error('This account has been removed. Contact support if you believe this is a mistake.', { id: 'account-deleted', duration: 8000 });
                }
            }
            finally {
                setInitializing(false);
            }
        }
        restoreSession();
    }, []);
    if (isInitializing) {
        return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-gray-50", children: _jsx("div", { className: "h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" }) }));
    }
    return _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=AuthProvider.js.map